import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../i18n/LanguageContext";
import { scrollSliderToNeighbor } from "../../utils/sliderNavigation";

type ServiceCard = {
  title: string;
  description: string;
};

const cardLayouts = [
  { accent: false, text: "bottom", shape: "leftTop" },
  { accent: false, text: "top", shape: "rightBottom" },
  { accent: true, text: "bottom", shape: "leftTop" },
  { accent: false, text: "top", shape: "leftBottom" },
  { accent: false, text: "bottom", shape: "rightTop" },
] as const;

const RibbonShape: React.FC<{ tone: "lime" | "white"; position: string }> = ({
  tone,
  position,
}) => {
  const isRight = position.includes("right");
  const isBottom = position.includes("Bottom");
  const imageStyle =
    tone === "white"
      ? { filter: "brightness(0) invert(1)", opacity: 0.8 }
      : { opacity: 0.45 };

  return (
    <div
      className={`pointer-events-none absolute h-[192px] w-[152px] ${
        isRight ? "right-0" : "left-0"
      } ${isBottom ? "bottom-0" : "top-0"}`}
      aria-hidden="true"
    >
      <img
        src="/images/frame-25.svg"
        alt=""
        aria-hidden="true"
        className={`absolute inset-0 h-full w-full object-contain ${
          isRight ? "scale-x-[-1]" : ""
        } ${isBottom ? "scale-y-[-1]" : ""}`}
        style={imageStyle}
      />
    </div>
  );
};

const ServicesPreviewSection: React.FC = () => {
  const { t, dir } = useLanguage();
  const sectionRef = useRef<HTMLElement | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const isRtl = dir === "rtl";

  const content = (t as any)?.homeServices || {};
  const cards = (content.items || []) as ServiceCard[];
  const mirrorPositionForRtl = (position: string) =>
    position.replace("left", "__temp__").replace("right", "left").replace("__temp__", "right");

  const scrollCards = (direction: "previous" | "next") => {
    scrollSliderToNeighbor({
      scroller: scrollerRef.current,
      direction,
      isRtl,
    });
  };

  useEffect(() => {
    const element = sectionRef.current;
    if (!element) return;

    if (!("IntersectionObserver" in window)) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "0px 0px -18% 0px", threshold: 0.2 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-stone-100 py-24 sm:py-28"
      aria-labelledby="home-services-title"
      dir={isRtl ? "rtl" : "ltr"}
    >
      <div
        className={`site-container relative z-10 flex flex-col gap-8 ${
          isRtl ? "items-end" : "items-start"
        }`}
      >
        <div
          className={`flex w-full items-end justify-between gap-6 ${
            isRtl ? "md:flex-row-reverse" : ""
          }`}
        >
          <div
            className={`flex max-w-2xl flex-col transition-all duration-700 ease-out ${
              isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
            } ${isRtl ? "items-end text-right" : "items-start text-left"}`}
          >
            <h2
              id="home-services-title"
              className="header-title uppercase text-teal-900"
            >
              {content.title || "Everything in one place."}
            </h2>
            <p
              className={`section-subtitle max-w-[467px] text-neutral-600 ${
                isRtl ? "text-right" : "text-left"
              }`}
            >
              {content.description ||
                "We handle the full journey from choosing the right curriculum to your child's first week of school."}
            </p>
          </div>

          <div
            className={`hidden items-center gap-3 transition-all duration-700 ease-out md:flex ${
              isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
            } ${isRtl ? "flex-row-reverse" : ""}`}
          >
            <button
              type="button"
              onClick={() => scrollCards("previous")}
              aria-label="Previous services"
              className="flex h-10 w-10 items-center justify-center border border-second-color text-second-color transition-colors hover:bg-second-color hover:text-main-color focus:outline-none focus:ring-2 focus:ring-main-color"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d={isRtl ? "M7.5 4L13.5 10L7.5 16" : "M12.5 4L6.5 10L12.5 16"}
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => scrollCards("next")}
              aria-label="Next services"
              className="flex h-10 w-10 items-center justify-center bg-second-color text-main-color transition-colors hover:bg-main-color hover:text-second-color focus:outline-none focus:ring-2 focus:ring-main-color"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d={isRtl ? "M12.5 4L6.5 10L12.5 16" : "M7.5 4L13.5 10L7.5 16"}
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="w-full overflow-x-auto pb-2 snap-x snap-mandatory [scrollbar-width:none]"
        >
          <div
            className={`grid min-w-max grid-cols-5 items-center gap-6 ${
              isRtl ? "[direction:rtl]" : "[direction:ltr]"
            }`}
          >
            {cards.map((card, index) => {
              const layout = cardLayouts[index % cardLayouts.length];
              const textAtBottom = layout.text === "bottom";
              const shapePosition = isRtl
                ? mirrorPositionForRtl(layout.shape)
                : layout.shape;

              return (
                <article
                  key={card.title}
                  className={`group relative h-[344px] w-[84vw] max-w-[320px] shrink-0 snap-start overflow-hidden transition-all duration-700 ease-out hover:-translate-y-2 sm:w-[280px] xl:h-96 xl:w-80 ${
                    layout.accent ? "bg-main-color" : "bg-white"
                  } ${
                    isVisible
                      ? "translate-y-0 rotate-0 opacity-100"
                      : "translate-y-10 rotate-1 opacity-0"
                  }`}
                  style={{ transitionDelay: `${index * 90}ms` }}
                >
                  <RibbonShape
                    tone={layout.accent ? "white" : "lime"}
                    position={shapePosition}
                  />
                  <div
                    className={`relative z-10 flex h-full w-full p-6 ${
                      textAtBottom ? "items-end" : "items-start"
                    }`}
                  >
                    <div
                      className={`flex max-w-64 flex-col gap-1 ${
                        isRtl ? "items-end text-right" : "items-start text-left"
                      }`}
                    >
                      <h3 className="font-sans text-lg font-semibold leading-6 text-teal-900 xl:text-xl">
                        {card.title}
                      </h3>
                      <p className="font-sans text-sm font-normal leading-4 text-teal-900">
                        {card.description}
                      </p>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>

        <div className={`w-full ${isRtl ? "text-right" : "text-left"}`}>
          <Link
            to="/services"
            data-property-1="prim"
            className="inline-flex min-h-10 w-full items-center justify-center gap-2.5 bg-second-color px-6 py-2 text-center font-sans text-base font-medium leading-6 text-main-color transition-colors hover:bg-second-color/90 focus:outline-none focus:ring-2 focus:ring-main-color focus:ring-offset-2 focus:ring-offset-stone-100 sm:w-fit"
          >
            {content.cta || "Explore our Services"}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServicesPreviewSection;
